/**
 * Discovery Home, one per role (release 3.1, persona D6; SPEC.md 7.1).
 *
 * Every page used to land on one generic Home: a marketing hero, four KPIs
 * read for every role (the Section 9 and decision tiles 403'd or showed
 * another role's data), module tiles for modules the caller could not open,
 * and links into the mock Innovation catalogue. Now the Home shows the
 * caller's role, the widgets SPEC.md 7.1 lists for it (`pages/home/role-home.ts`,
 * each reading a call of a page the caller may open, so none can 403), and the
 * pages the caller may open, grouped as in the sidebar. A custom role gets the
 * widgets its grants cover.
 */
import { Rx } from '@/lib/regulation-terms';
import { motion } from 'motion/react';
import { Link } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { ArrowRight, Compass } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { GlassCard } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { PageContainer } from '@/components/viz/PageHeader';
import { HelpTip } from '@foctta/ui-shell';
import { DemoTourButton } from '@/components/tour/DemoTourButton';
import { visibleNav } from '@/components/shell/SideNav';
import { builtInRoleOf, DASHBOARDS, pageById, type Caller } from '@/generated/persona/discovery-access';
import { canOpen, useCaller } from '@/lib/access';
import { getAuthUser } from '@/lib/api';
import { friendlyName } from '@/lib/display-name';
import { ALL_WIDGETS, ROLE_HOME, type HomeWidget, type WidgetValue } from './home/role-home';
import { LeadershipSummary } from './home/LeadershipSummary';

/** The widgets `caller` sees: its role's list (a custom role: all), only those whose page it may open. */
export function widgetsFor(caller: Caller): HomeWidget[] {
  const role = builtInRoleOf(caller.role);
  const list = role ? ROLE_HOME[role] : ALL_WIDGETS;
  const seen = new Set<string>();
  return list.filter((w) => {
    if (seen.has(w.id) || !canOpen(w.pageId, caller)) return false;
    seen.add(w.id);
    return true;
  });
}

/**
 * Release 8, C6: the roles that read for the board see the leadership summary on
 * their Home: the DPO and the Auditor (read-only security operations, where the
 * CISO and executive briefings live). It reads the same data as the board-report
 * area, so it is offered only to a caller who may open that area. It sits BELOW
 * the role's own action tiles: what needs doing stays on the first screen.
 */
export function showsLeadershipSummary(caller: Caller): boolean {
  const role = builtInRoleOf(caller.role);
  return (role === 'dpo' || role === 'read_only_security_operations') && canOpen('disc.comp_coverage', caller);
}

function WidgetCard({ widget }: { widget: HomeWidget }) {
  const [state, setState] = useState<{ loading: boolean; value: WidgetValue | null; error: string | null }>({
    loading: true,
    value: null,
    error: null,
  });
  useEffect(() => {
    let live = true;
    widget.load().then(
      (value) => live && setState({ loading: false, value, error: null }),
      (err: unknown) =>
        live && setState({ loading: false, value: null, error: err instanceof Error ? err.message : 'could not load' }),
    );
    return () => {
      live = false;
    };
  }, [widget]);
  const page = pageById(widget.pageId);
  const to = page?.routes.find((r) => !r.includes('$')) ?? '/';
  return (
    <GlassCard data-widget-id={widget.id} className="p-4 space-y-2">
      <div className="text-xs uppercase tracking-wider text-muted-foreground"><Rx>{widget.title}</Rx></div>
      <div className="text-2xl font-display font-semibold" aria-live="polite">
        {state.loading ? '...' : state.value ? state.value.value : 'Not available'}
      </div>
      <div className="text-xs text-muted-foreground min-h-4">
        {state.error ? `Could not load: ${state.error}` : (state.value?.detail ?? '')}
      </div>
      <Link to={to} className="inline-flex items-center gap-1 text-xs text-primary hover:underline">
        Open {page?.label ?? 'page'} <ArrowRight className="h-3 w-3" />
      </Link>
    </GlassCard>
  );
}

export function HomePage() {
  const access = useCaller();
  // The route guard renders nothing but "checking your access" until the
  // caller is known, so this is only a type narrowing.
  if (access.status !== 'ready') return null;
  const { caller, me } = access;
  const user = getAuthUser();
  const firstName = friendlyName(user?.displayName, user?.email).first;
  const role = builtInRoleOf(caller.role);
  const focus = role ? DASHBOARDS[role] : undefined;
  const widgets = widgetsFor(caller);
  const groups = visibleNav(caller);

  return (
    <PageContainer className="space-y-4" data-home-role={role ?? caller.role}>
      {/* One row: the greeting, the role, and what this Home shows behind a "?".
          It was a 36 px greeting over a sentence listing the tiles below it. */}
      <motion.section
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, ease: [0.32, 0.72, 0, 1] }}
        className="flex flex-wrap items-center justify-between gap-3"
      >
        <div className="flex min-w-0 flex-wrap items-center gap-2">
          <h1 className="text-2xl font-display font-bold tracking-tight">
            {firstName ? `Welcome back, ${firstName}.` : 'Welcome back.'}
          </h1>
          <Badge variant="glass" data-testid="home-role">
            {me.roleLabel}
          </Badge>
          {focus && focus.length > 0 && (
            <HelpTip label="What your Home shows">Your Home: {focus.join('; ')}.</HelpTip>
          )}
        </div>
        <div className="flex flex-wrap gap-2">
          {/* The sales tour is offered to a DEMO tenant only (DemoTourButton, tour-policy.ts). */}
          <DemoTourButton primary={false} />
          {/* Operator walkthrough of the admin and compliance surfaces. */}
          <Button size="sm" variant="glass" asChild>
            <Link to="/" search={{ demo: 'ops' } as Record<string, unknown>}>
              <Compass className="h-4 w-4" /> Operator walkthrough
            </Link>
          </Button>
        </div>
      </motion.section>

      <section aria-label="Your overview">
        {widgets.length === 0 ? (
          <p className="text-sm text-muted-foreground" data-testid="home-no-widgets">
            There is nothing for your role to review in Discovery yet.
          </p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
            {widgets.map((w) => (
              <WidgetCard key={w.id} widget={w} />
            ))}
          </div>
        )}
      </section>

      {showsLeadershipSummary(caller) && (
        <section aria-label="Leadership summary" data-testid="home-leadership">
          <LeadershipSummary />
        </section>
      )}

      {/* The same pages the sidebar and "Jump to" offer, so shut until asked
          for: listed open it was two more screens under the tiles. */}
      {groups.length > 0 && (
        <details aria-label="Your pages" data-testid="home-pages">
          <summary className="cursor-pointer select-none text-xs uppercase tracking-wider text-muted-foreground hover:text-foreground">
            Your pages ({groups.reduce((n, g) => n + g.items.length, 0)})
          </summary>
          <div className="mt-3 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {groups.map((g) => (
              <GlassCard key={g.id} className="p-4">
                <div className="text-sm font-semibold mb-2">{g.label}</div>
                <ul className="space-y-1">
                  {g.items.map((item) => (
                    <li key={item.pageId}>
                      <Link to={item.to} className="text-sm text-muted-foreground hover:text-foreground">
                        {item.label}
                      </Link>
                    </li>
                  ))}
                </ul>
              </GlassCard>
            ))}
          </div>
        </details>
      )}
    </PageContainer>
  );
}
